"use client";

import {memo, useCallback} from "react";

import {useSnapshot} from "valtio";

import {type DateRangePickerInstance} from "@/shared/models/form/datepickers";
import {Calendar, type CalendarDay} from "@/shared/ui/form/datepickers/parts";

interface SingleCalendarProps {
    model: DateRangePickerInstance;
    onDayClick: (day: CalendarDay) => void;
    onDayHover: (date: Date) => void;
    onDayLeave: () => void;
    getDayTooltip?: (date: Date) => string | null | undefined;
    getDayClassName?: (date: Date) => string | null | undefined;
    hideOtherMonthDaysContent?: boolean;
}

export const SingleCalendar = memo(function SingleCalendar({
    model,
    onDayClick,
    onDayHover,
    onDayLeave,
    getDayTooltip,
    getDayClassName,
    hideOtherMonthDaysContent,
}: SingleCalendarProps) {
    const snapshot = useSnapshot(model);

    const handlePreviousYear = useCallback(() => model.previousYear(), [model]);
    const handlePreviousMonth = useCallback(() => model.previousMonth(), [model]);
    const handleNextMonth = useCallback(() => model.nextMonth(), [model]);
    const handleNextYear = useCallback(() => model.nextYear(), [model]);

    return (
        <Calendar
            month={snapshot.viewMonth}
            year={snapshot.viewYear}
            weekdays={snapshot.weekdays}
            days={snapshot.calendarDays}
            showLeftNav={true}
            disableLeftNav={snapshot.isAtMinMonth}
            disablePreviousYear={snapshot.isAtMinYear}
            showRightNav={true}
            onPreviousYear={handlePreviousYear}
            onPreviousMonth={handlePreviousMonth}
            onNextMonth={handleNextMonth}
            onNextYear={handleNextYear}
            onDayClick={onDayClick}
            onDayHover={onDayHover}
            onDayLeave={onDayLeave}
            getDayTooltip={getDayTooltip}
            getDayClassName={getDayClassName}
            hideOtherMonthDaysContent={hideOtherMonthDaysContent}
        />
    );
});
